import {
  ArrowClockwiseIcon,
  ArrowUpIcon,
  CaretDownIcon,
  CheckIcon,
  SparkleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';

import { TextDiff } from '@/components/ai-elements/text-diff';
import { Button } from '@/components/ui/button';
import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { useDialogContentContainer } from '@/components/ui/dialog';
import { CHIP_HEIGHT, PANEL_POP_IN_MOTION, SECTION_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import type { SelectionAction } from '@/services/content/selection-action';
import {
  SELECTION_INSTRUCTION_MAX_LENGTH,
  runSelectionAction,
} from '@/services/content/selection-action';

/**
 * Floating toolbar for a text selection inside a plain textarea/input —
 * Explain/Improve/Shorten/Fix grammar/Change tone acting on a highlighted
 * phrase. Anchored to the FIELD's bounding rect, not the precise pixel
 * position of the substring (see use-text-selection-actions.ts for why).
 *
 * Scope, explicitly: ad copy, captions, brand voice, briefs, outreach
 * drafts — plain `<textarea>`/`<input>` surfaces. NEVER the Home composer.
 * That input is Tiptap/contentEditable (a different selection model that
 * needs a ProseMirror bubble-menu plugin, not this hook) and, separately,
 * the founder confirmed a selection toolbar wouldn't make sense there —
 * "that would not really make sense at all in this context."
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ATTRIBUTION for the two opt-in behaviours below. `allowFreeform` and
 * `confirmRewrite` are hand-ported from `components/primitives/SelectionActions.tsx`
 * in the "beautiful-ui" reference kit — MIT, Copyright (c) 2026 Shane Levine.
 * The INTERACTION is theirs: a free-text edit request living inside the same
 * pill as the preset verbs, secondary verbs behind a disclosure so the pill
 * stays narrow, and a rewrite that lands as a proposal with Keep / Discard /
 * Retry rather than silently replacing what the user wrote.
 *
 * Nothing else was taken. The component was NOT forked — Mora already owned the
 * toolbar, the hook (`use-text-selection-actions.ts`) and a real backend
 * (`/api/content/selection-action`), where the source's is a scripted demo with
 * three hardcoded strings. Their `iconoir-react` glyphs map to Phosphor v2
 * (`Spark`→`SparkleIcon`, `ArrowUp`→`ArrowUpIcon`, `NavArrowRight`→
 * `CaretDownIcon`, `Check`→`CheckIcon`, `Xmark`→`XIcon`, `Refresh`→
 * `ArrowClockwiseIcon`), their `Shimmer`/`StreamText` atoms are unused here
 * (this endpoint returns one shot, it does not stream), their `bg-ink`/
 * `text-canvas` pill is Mora's `Button`, and their width-morph `Animation`
 * choreography is dropped outright: it is 90 lines of `element.animate()` and
 * `ResizeObserver` bookkeeping in service of a pill that changes width, which
 * `prefers-reduced-motion` would have to undo anyway.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const ACTIONS: { id: SelectionAction; label: string }[] = [
  { id: 'improve', label: 'Improve' },
  { id: 'shorten', label: 'Shorten' },
  { id: 'grammar', label: 'Fix grammar' },
  { id: 'tone', label: 'Change tone' },
  { id: 'explain', label: 'Explain' },
];

/**
 * The two verbs that stay visible when `allowFreeform` adds a text field to the
 * pill. Everything else moves behind the disclosure — five labels plus a field
 * plus a send button is ~500px, which does not fit a 390px viewport, and a
 * floating toolbar that overflows its own screen is worse than one extra tap.
 */
const PRIMARY_ACTION_IDS = new Set<SelectionAction>(['improve', 'explain']);

const TONE_OPTIONS = ['Professional', 'Casual', 'Playful', 'Confident'];

const ACTION_BUTTON =
  'whitespace-nowrap rounded-full px-2.5 py-1 text-caption font-medium text-foreground/80 transition-colors hover:bg-muted disabled:opacity-40';

type PendingRun = { action: SelectionAction; tone?: string; instruction?: string };

export interface TextSelectionToolbarProps {
  /**
   * Native field anchor used by textarea/input callers. Rich editors can
   * instead provide `getAnchorRect`, keeping this one toolbar and one rewrite
   * service across both selection models.
   */
  fieldRef?: React.RefObject<HTMLTextAreaElement | HTMLInputElement>;
  /** Exact viewport rect for a contenteditable/ProseMirror selection. */
  getAnchorRect?: () => DOMRect | null;
  selectedText: string;
  /** Full field value, sent as reference context for the model — never edited itself. */
  context?: string;
  accessToken: string | null;
  /** Opts this surface into the account's saved brand voice for rewrite actions. */
  useBrandVoice?: boolean;
  onApply(newText: string): void;
  onDismiss(): void;
  /**
   * Adds a "Describe edits" field to the pill so the user can ask for the edit
   * in their own words instead of translating it into the nearest preset verb.
   * Routes to the `instruction` action, which the endpoint validates and caps —
   * it is NOT a preset verb wearing a text field.
   *
   * Off by default: it also collapses the three secondary verbs behind a
   * disclosure, which is a real change to a surface that already ships.
   */
  allowFreeform?: boolean;
  /**
   * Hold the rewrite as a PROPOSAL — a word-level diff against the selection,
   * with Keep / Discard / Retry — instead of splicing it straight into the
   * field. Off by default so the existing caller is untouched, but it is the
   * better default for any new surface: a rewrite the reader never saw arrive
   * is indistinguishable from the field losing their words.
   */
  confirmRewrite?: boolean;
  /** Prefills the freeform request when a review finding opens this toolbar. */
  initialInstruction?: string;
}

export function TextSelectionToolbar({
  fieldRef,
  getAnchorRect,
  selectedText,
  context,
  accessToken,
  useBrandVoice = false,
  onApply,
  onDismiss,
  allowFreeform = false,
  confirmRewrite = false,
  initialInstruction,
}: TextSelectionToolbarProps): JSX.Element | null {
  // Absolute { left, top } for the pill's own top-left corner — never a
  // translate(-50%, X) offset. That indirection is exactly what broke this:
  // `rect.top - 8` combined with `translateY(-100%)` can push the pill's
  // rounded/bordered/shadowed top edge above y=0 whenever the field sits
  // anywhere near the top of its panel (common in an Inspector rail), so
  // only a clipped sliver of unstyled text showed. Computing final
  // coordinates directly makes clamping to the viewport a plain min/max.
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const [pending, setPending] = useState<SelectionAction | null>(null);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [toneOpen, setToneOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [instruction, setInstruction] = useState(initialInstruction ?? '');
  const instructionTooLong = instruction.trim().length > SELECTION_INSTRUCTION_MAX_LENGTH;
  // The last run, kept so Retry can repeat it verbatim — a retry that quietly
  // fell back to "improve" would be a different edit wearing the same button.
  const [lastRun, setLastRun] = useState<PendingRun | null>(null);
  const [proposal, setProposal] = useState<string | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  // Inside a modal dialog (the Inspector's document workspace) a pill portaled to document.body is a DOM sibling of
  // the dialog: Radix sets `pointer-events: none` on the body, so every click on the pill fell through to the page
  // beneath it (collapsing the selection and removing the pill), and the focus trap and outside-click dismissal
  // treat it as outside. Rendering into the dialog's own content node is the codebase's established fix
  // (`useDialogContentContainer`, as dropdown-menu and select do), and it is then positioned against that node.
  const container = useDialogContentContainer();

  useLayoutEffect(() => {
    const margin = 8;
    const reposition = () => {
      const rect = getAnchorRect?.() ?? fieldRef?.current?.getBoundingClientRect();
      if (!rect) return;
      // Real measured size once rendered; a reasonable estimate before the
      // first paint so there's no flash of an unpositioned/zero-size pill.
      const toolbarRect = toolbarRef.current?.getBoundingClientRect();
      const height = toolbarRect?.height ?? 40;
      const width = toolbarRect?.width ?? 320;

      const above = rect.top - margin - height >= margin;
      const rawTop = above ? rect.top - margin - height : rect.bottom + margin;
      const rawLeft = rect.left + rect.width / 2 - width / 2;

      const top = Math.min(Math.max(rawTop, margin), window.innerHeight - height - margin);
      const left = Math.min(Math.max(rawLeft, margin), window.innerWidth - width - margin);
      // In a container the pill is `absolute` against it (a transformed container would also re-base `fixed`).
      const origin = container?.getBoundingClientRect();
      setPosition({
        top: top - (origin ? origin.top - container!.scrollTop : 0),
        left: left - (origin ? origin.left - container!.scrollLeft : 0),
      });
    };
    reposition();
    // Second pass once the pill has actually rendered: the first pass used
    // the height/width estimate above, this corrects against the real size.
    const raf = requestAnimationFrame(reposition);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
    // `proposal` re-measures: the proposal panel is a different size from the
    // pill it replaces, so without this the panel keeps the pill's coordinates.
  }, [fieldRef, getAnchorRect, proposal, container]);

  const runAction = async (run: PendingRun) => {
    if (pending) return;
    if (!accessToken) {
      // A silent return here made Send look like it did nothing at all.
      toast.error('Sign in again to request an AI edit.');
      return;
    }
    setPending(run.action);
    setToneOpen(false);
    try {
      const result = await runSelectionAction({
        text: selectedText,
        action: run.action,
        tone: run.tone,
        instruction: run.instruction,
        context,
        useBrandVoice,
        accessToken,
      });
      setLastRun(run);
      if (run.action === 'explain') {
        setExplanation(result);
      } else if (result.trim() === selectedText.trim()) {
        toast.info('No change suggested — this selection already fits the request.');
        onDismiss();
      } else if (confirmRewrite) {
        setProposal(result);
      } else {
        onApply(result);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Selection action failed');
    } finally {
      setPending(null);
    }
  };

  if (!position) return null;

  const visibleActions =
    allowFreeform && !moreOpen ? ACTIONS.filter((a) => PRIMARY_ACTION_IDS.has(a.id)) : ACTIONS;

  const panelShell =
    'rounded-xl border border-border/60 bg-popover/95 p-3 shadow-elevation-3 backdrop-blur-xl';

  const renderBody = () => {
    if (explanation) {
      return (
        <div className={cn('w-72', panelShell, PANEL_POP_IN_MOTION)}>
          <p className="text-caption text-foreground/90">{explanation}</p>
          <button
            type="button"
            onClick={() => {
              setExplanation(null);
              onDismiss();
            }}
            className="mt-2 text-detail text-muted-foreground hover:text-foreground"
          >
            Got it
          </button>
        </div>
      );
    }

    // The proposal, not the rewrite. Nothing has touched the field yet; Keep is
    // the only path that does, and Discard leaves the user's own words standing.
    if (proposal !== null) {
      return (
        <div className={cn('w-80', panelShell, PANEL_POP_IN_MOTION)}>
          <p className={SECTION_LABEL}>Suggested edit</p>
          <TextDiff before={selectedText} after={proposal} className="mt-2 text-caption" />
          <div className="mt-3 flex items-center gap-1.5">
            <Button
              type="button"
              size="sm"
              onClick={() => {
                onApply(proposal);
                setProposal(null);
              }}
            >
              <CheckIcon weight="regular" className="size-3.5" />
              Keep
            </Button>
            <Button
              type="button"
              size="sm"
              variant="flat"
              onClick={() => {
                setProposal(null);
                onDismiss();
              }}
            >
              Discard
            </Button>
            <CircleIconButton
              size="sm"
              ariaLabel="Try again"
              disabled={pending !== null || !lastRun}
              onClick={() => {
                if (!lastRun) return;
                setProposal(null);
                void runAction(lastRun);
              }}
              className="ml-auto text-muted-foreground/60 hover:bg-muted hover:text-foreground"
            >
              <ArrowClockwiseIcon weight="regular" />
            </CircleIconButton>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-0.5 rounded-full border border-border/60 bg-popover/95 p-1 shadow-elevation-3 backdrop-blur-xl">
        {/* The field yields to the secondary verbs rather than sitting beside
            them: both at once is a pill wider than a phone. */}
        {allowFreeform && !moreOpen ? (
          <form
            className="flex shrink-0 items-center"
            onSubmit={(event) => {
              event.preventDefault();
              const typed = instruction.trim();
              if (!typed || instructionTooLong) return;
              setInstruction('');
              void runAction({ action: 'instruction', instruction: typed });
            }}
          >
            <input
              // The PILL owns the surface; this control owns only its text.
              // Without the slot, index.css's unlayered
              // `[data-theme='dark'] input { background-color: hsl(var(--input))
              //  !important }` paints a filled zinc slab inside the popover —
              // measured rgb(39,39,42) at 390x844 dark, 2026-08-27. That rule
              // exempts exactly this slot, and exempting it is the contract the
              // rule itself documents, not an override of it.
              data-slot="input-group-control"
              value={instruction}
              onChange={(event) => setInstruction(event.target.value)}
              // No `maxLength`: the browser truncates typed and pasted text
              // silently, which spliced half a sentence into an article. Over
              // the cap the count shows and nothing is sent.
              aria-invalid={instructionTooLong || undefined}
              aria-label="Describe edits"
              placeholder="Describe edits"
              disabled={pending !== null}
              className={cn(
                CHIP_HEIGHT,
                'w-32 min-w-0 bg-transparent px-2.5 text-caption text-foreground outline-none placeholder:text-muted-foreground/70 disabled:opacity-40',
              )}
            />
            {instructionTooLong ? (
              <span role="status" className="shrink-0 px-1.5 text-detail text-destructive">
                {instruction.trim().length}/{SELECTION_INSTRUCTION_MAX_LENGTH}
              </span>
            ) : instruction.trim() ? (
              <CircleIconButton
                size="sm"
                type="submit"
                ariaLabel="Send edit instruction"
                className="bg-neutral text-neutral-foreground hover:bg-neutral-hover hover:text-neutral-foreground"
              >
                <ArrowUpIcon weight="regular" />
              </CircleIconButton>
            ) : null}
            <span className="mx-1 h-3.5 w-px shrink-0 bg-border/60" />
          </form>
        ) : null}

        {visibleActions.map((action) =>
          action.id === 'tone' ? (
            <div key={action.id} className="relative">
              <button
                type="button"
                disabled={pending !== null}
                onClick={() => setToneOpen((v) => !v)}
                className={cn(ACTION_BUTTON, pending === action.id && 'text-muted-foreground')}
              >
                {pending === action.id ? 'Working…' : action.label}
              </button>
              {toneOpen ? (
                <div className="absolute left-1/2 top-full z-popover mt-1 w-36 -translate-x-1/2 rounded-lg border border-border/60 bg-popover p-1 shadow-elevation-2">
                  {TONE_OPTIONS.map((toneOption) => (
                    <button
                      key={toneOption}
                      type="button"
                      onClick={() =>
                        void runAction({ action: 'tone', tone: toneOption.toLowerCase() })
                      }
                      className="block w-full rounded-md px-2 py-1.5 text-left text-caption text-foreground/85 hover:bg-muted"
                    >
                      {toneOption}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <button
              key={action.id}
              type="button"
              disabled={pending !== null}
              onClick={() => void runAction({ action: action.id })}
              className={cn(ACTION_BUTTON, action.id === 'explain' && 'flex items-center gap-1')}
            >
              {action.id === 'explain' && <SparkleIcon className="size-3" weight="duotone" />}
              {pending === action.id ? 'Working…' : action.label}
            </button>
          ),
        )}

        {allowFreeform ? (
          <CircleIconButton
            size="sm"
            ariaLabel={moreOpen ? 'Show fewer actions' : 'Show more actions'}
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen((v) => !v)}
            className="text-muted-foreground/70 hover:bg-muted hover:text-foreground"
          >
            <CaretDownIcon
              weight="regular"
              className={cn('transition-transform duration-150', moreOpen && 'rotate-180')}
            />
          </CircleIconButton>
        ) : null}

        <CircleIconButton
          size="sm"
          ariaLabel="Dismiss"
          onClick={onDismiss}
          className="text-muted-foreground/50 hover:bg-muted hover:text-foreground"
        >
          <XIcon className="size-3" weight="bold" />
        </CircleIconButton>
      </div>
    );
  };

  return createPortal(
    <div
      // A toolbar button's mousedown must never steal focus from the field —
      // that's what keeps the tracked selection (and its pixel position)
      // alive across the click. preventDefault here covers every button in
      // one place instead of each one repeating it.
      //
      // The freeform field is the one exception: it has to be able to TAKE
      // focus, so its own mousedown stops the bubble before this handler runs.
      onMouseDown={(event) => {
        if (event.target instanceof HTMLInputElement) return;
        event.preventDefault();
      }}
      ref={toolbarRef}
      // Read by `useTextSelectionActions`'s onBlur: focus moving INTO the
      // toolbar (the freeform field) must not count as leaving the selection.
      data-selection-toolbar=""
      style={{
        position: container ? 'absolute' : 'fixed',
        left: position.left,
        top: position.top,
        pointerEvents: 'auto',
      }}
      className="z-popover"
    >
      {renderBody()}
    </div>,
    container ?? document.body,
  );
}
